import {
	isContestantData,
	isDomainShortcutData,
	isMemberData,
	isSportData,
} from "./suggestionTypeGuards";

export interface SuggestionWithData {
	data?: unknown;
	label: string;
}

/**
 * Extracts a clean value from a suggestion data object.
 * This utility provides type-safe extraction of values from autocomplete suggestions.
 */
export const extractValueFromSuggestion = (suggestion: SuggestionWithData): string => {
	// Extract the actual name/value from the data, not the formatted label
	if (suggestion.data) {
		// For domain shortcuts, use the formatted domain shortcut value
		if (isDomainShortcutData(suggestion.data) && suggestion.data.domainShortcutValue) {
			return suggestion.data.domainShortcutValue;
		}

		// For members (players), extract number and name if available
		if (isMemberData(suggestion.data)) {
			const { name, number } = suggestion.data;
			if (number && number !== "" && name) {
				return `${number} ${name}`;
			}
			if (name) {
				return name;
			}
		}

		// For contestants (teams), extract name or shortName
		if (isContestantData(suggestion.data)) {
			return suggestion.data.shortName || suggestion.data.name || "";
		}

		// For sport info
		if (isSportData(suggestion.data) && suggestion.data.sport) {
			return suggestion.data.sport;
		}
	}

	// Fallback to label if no specific data structure
	return suggestion.label;
};

/**
 * Resolves a suggestion to the image it carries: a member's photo or a team's
 * logo. Used by image fields, where the value is a URL rather than a name.
 */
export const extractImageUrlFromSuggestion = (suggestion: SuggestionWithData): string => {
	if (isMemberData(suggestion.data) && suggestion.data.photoUrl) {
		return suggestion.data.photoUrl;
	}

	if (isContestantData(suggestion.data) && suggestion.data.imageUrl) {
		return suggestion.data.imageUrl;
	}

	return "";
};

/** True when a suggestion resolves to an image, so image fields can hide the rest. */
export const hasImageSuggestion = (suggestion: SuggestionWithData): boolean =>
	extractImageUrlFromSuggestion(suggestion) !== "";

/**
 * Simplified version for repeater cells that excludes number formatting.
 * Used in repeater components where player numbers are not needed.
 */
export const extractValueFromSuggestionSimple = (suggestion: SuggestionWithData): string => {
	// Extract the actual name/value from the data, not the formatted label
	if (suggestion.data) {
		// For domain shortcuts, use the formatted domain shortcut value
		if (isDomainShortcutData(suggestion.data) && suggestion.data.domainShortcutValue) {
			return suggestion.data.domainShortcutValue;
		}

		// For members (players), extract just the name (no number)
		if (isMemberData(suggestion.data) && suggestion.data.name) {
			return suggestion.data.name;
		}

		// For contestants (teams), extract name or shortName
		if (isContestantData(suggestion.data)) {
			return suggestion.data.shortName || suggestion.data.name || "";
		}

		// For sport info
		if (isSportData(suggestion.data) && suggestion.data.sport) {
			return suggestion.data.sport;
		}
	}

	// Fallback to label if no specific data structure
	return suggestion.label;
};
